'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import {
  fetchCountries,
} from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useForm, useFormState } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { Paragraph } from '../ui/paragraph';
import { MdAlternateEmail } from 'react-icons/md';
import { BsKey } from 'react-icons/bs';
import { IoMdArrowDropdown } from 'react-icons/io';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';

type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;
export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      company_buy: customer?.company_buy ?? false,
      street_number: customer?.street_number ?? '',
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_company: customer?.address_company ?? '',
      street_number_company: customer?.street_number_company ?? '',
      postcode_company: customer?.postcode_company ?? '',
      city_company: customer?.city_company ?? '',
      country_company: customer?.country_company ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      }).then(res => {
        const typedRes = res as { data: Customer };
        typedRes &&
          initCustomerStore({
            authToken,
            customer: { ...typedRes?.data },
          });
        return typedRes;
      });

      router.refresh();
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  const isCompany = form.watch('company_buy');

  return (
    <Form {...form}>
      <form
        className="flex w-full flex-col items-start justify-start gap-8 sm:gap-10 lg:gap-12"
        onSubmit={handleSubmit}
      >
        <div className={cn('flex w-full flex-col items-start justify-start gap-2')}>
          <h5 className="text-[28px] font-medium text-black sm:text-3xl lg:text-[36px]">
            {customer.name} {customer.surname}
          </h5>
          <Paragraph>{customer.email}</Paragraph>
        </div>
        <Section>
          <Field
            required
            control={form.control}
            name="name"
            label={t('text33')}
            className={{ wrapper: 'col-span-12 sm:col-span-6' }}
          />
          <Field
            required
            control={form.control}
            name="surname"
            label={t('text34')}
            className={{ wrapper: 'col-span-12 sm:col-span-6' }}
          />
        </Section>
        <Section>
          <SectionHeader title={t('text360')} />
          <Field
            required
            control={form.control}
            name="email"
            type="email"
            label={t('text35')}
            before={<MdAlternateEmail size={24} className="text-[#9E9E9E]" />}
            className={{ wrapper: 'col-span-12 sm:col-span-6' }}
          />
          <Field
            control={form.control}
            name="phone"
            label={t('text36')}
            before={<BsKey size={24} className="text-[#9E9E9E]" />}
            className={{ wrapper: 'col-span-12 sm:col-span-6' }}
          />
        </Section>
        <Section>
          <SectionHeader title={t('text157')} />
          <Field
            control={form.control}
            name="address"
            label={t('text38')}
            className={{ wrapper: 'col-span-12 sm:col-span-8' }}
          />
          <Field
            control={form.control}
            name="street_number"
            label={t('text438')}
            className={{ wrapper: 'col-span-12 sm:col-span-4' }}
          />
          <Field
            control={form.control}
            name="postcode"
            label={t('text40')}
            className={{ wrapper: 'col-span-12 sm:col-span-4' }}
          />
          <Field
            control={form.control}
            name="city"
            label={t('text39')}
            className={{ wrapper: 'col-span-12 sm:col-span-8' }}
          />
          <Field
            control={form.control}
            type="select"
            name="country.id"
            label={t('text519')}
            isDisabled={isCountriesLoading}
            className={{ wrapper: 'col-span-12' }}
            options={countries.data.map(c => ({
              value: c.id,
              label: c.name,
            }))}
          />
        </Section>
        <Section>
          <FormField
            control={form.control}
            name="company_buy"
            render={({ field }) => (
              <FormItem className="col-span-12 flex flex-row items-center gap-4">
                <FormControl>
                  <Checkbox
                    checked={!!field.value}
                    onCheckedChange={val => field.onChange(Boolean(val))}
                  />
                </FormControl>
                <FormLabel className="m-0 text-base font-medium">{t('text2362')}</FormLabel>
              </FormItem>
            )}
          />
        </Section>

        {isCompany && (
          <Section>
            <SectionHeader title={t('text679')} />
            <Field
              control={form.control}
              name="ico"
              label={t('text42')}
              className={{ wrapper: 'col-span-12 sm:col-span-6' }}
            />
            <Field
              control={form.control}
              name="dic"
              label={t('text43')}
              className={{ wrapper: 'col-span-12 sm:col-span-6' }}
            />
            <Field
              control={form.control}
              name="ic_dph"
              label={t('text44')}
              className={{ wrapper: 'col-span-12' }}
            />
            <Field
              control={form.control}
              name="company"
              label={t('text37')}
              className={{ wrapper: 'col-span-12' }}
            />
            <Field
              control={form.control}
              name="address_company"
              label={t('text38')}
              className={{ wrapper: 'col-span-12 sm:col-span-8' }}
            />
            <Field
              control={form.control}
              name="street_number_company"
              label={t('text438')}
              className={{ wrapper: 'col-span-12 sm:col-span-4' }}
            />
            <Field
              control={form.control}
              name="postcode_company"
              label={t('text40')}
              className={{ wrapper: 'col-span-12 sm:col-span-4' }}
            />
            <Field
              control={form.control}
              name="city_company"
              label={t('text39')}
              className={{ wrapper: 'col-span-12 sm:col-span-8' }}
            />
            <Field
              control={form.control}
              type="select"
              name="country_company.id"
              label={t('text519')}
              isDisabled={isCountriesLoading}
              className={{ wrapper: 'col-span-12' }}
              options={countries.data.map(c => ({
                value: c.id,
                label: c.name,
              }))}
            />
          </Section>
        )}

        <div className="col-span-12 flex w-full flex-row justify-start">
          <Button
            type="submit"
            className="w-full md:w-auto"
            isLoading={form.formState.isSubmitting}
          >
            {t('text2290')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function Section({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn('grid w-full grid-cols-12 gap-4 sm:gap-5 lg:gap-6', className)}>
      {children}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <>
      <h4 className="col-span-12 w-full text-2xl font-medium tracking-tighter sm:text-[28px]">
        {title}
      </h4>
    </>
  );
}

type FieldProps = {
  control: Control<ProfileEditFormValues>;
  name: FieldPath<ProfileEditFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'tel' | 'radio';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
  withoutLabel?: boolean;
  description?: string | React.ReactNode;
  before?: React.ReactNode;
  className?: {
    item?: string;
    main?: string;
    wrapper?: string;
  };
};

function Field(props: FieldProps) {
  const { errors } = useFormState({ control: props.control });

  const content = useMemo(() => {
    const isError = errors[props.name];

    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem
                className={cn(
                  'relative min-h-[56px]',
                  props.label && 'flex flex-col items-start justify-start gap-3',
                )}
              >
                <FormControl>
                  <div className={cn('relative', props.label && 'w-full')}>
                    <Select
                      value={String(field.value)}
                      onValueChange={val => {
                        field.onChange(val);
                      }}
                      disabled={props.isDisabled}
                    >
                      <SelectTrigger
                        className={cn(
                          'min-h-[56px] cursor-pointer rounded-[8px] border-none bg-[#F9FAFB] !px-0 text-sm font-normal text-black sm:text-base',
                        )}
                        onClear={() => field.onChange(null)}
                        innerTriggerClassName="pl-2 pr-4"
                        clearableIconPossition="right"
                        expandIcon={<IoMdArrowDropdown size={24} className="text-primary" />}
                      >
                        {!props.withoutLabel && (
                          <FormLabel
                            htmlFor={props.name}
                            className={cn(
                              'absolute left-4 top-4 z-[1]  cursor-pointer select-none font-normal text-[#9E9E9E] transition-all duration-100',
                              field.value && field.value !== ''
                                ? '!top-[10px] text-xs'
                                : 'text-base',
                              isError && 'text-destructive',
                            )}
                            isRequired={false}
                          >
                            {!!props.label && `${props.label}${props.required ? '*' : ''}`}
                          </FormLabel>
                        )}
                        <div
                          className={
                            field.value && field.value !== '' && !props.withoutLabel
                              ? 'pl-4 pt-[20px]'
                              : 'pl-4'
                          }
                        >
                          <SelectValue placeholder={props.placeholder} className="pl-[30px]" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {props.options?.map?.(o => (
                          <SelectItem key={o.value} value={String(o.value)}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </FormControl>

                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4 pl-2"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                        <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                          <p className="text-sm font-semibold text-foreground">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormLabel>{props.label}</FormLabel>
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'textarea') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem
              required={props.required}
              className={cn(
                'min-h-[56px]',
                props.label && 'flex flex-col items-start justify-start gap-3',
              )}
            >
              {props.label && (
                <Label className="text-base font-bold text-foreground sm:text-lg">
                  {props.label}
                </Label>
              )}
              <FormControl className="min-h-[56px]">
                <Textarea
                  id={props.name}
                  placeholder={props.placeholder}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  rows={8}
                  className={cn(
                    'w-full rounded-lg border border-border bg-input p-5 text-base',
                    isError && 'border-destructive text-destructive',
                  )}
                />
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem
            className={cn('relative h-[56px] max-h-[56px] min-h-[56px]', props.className?.item)}
          >
            <FormControl>
              <div className="relative">
                <FormLabel
                  htmlFor={props.name}
                  className={cn(
                    'absolute top-4 z-[1] flex cursor-text select-none items-center gap-2 font-normal text-[#9E9E9E] transition-all duration-100',
                    field.value && field.value != '' ? '!top-[8.5px] text-xs' : 'text-base',
                    props.before ? 'left-12' : 'left-4',
                    isError && 'text-destructive',
                  )}
                  // Always set to false, we add start manually because of color
                  isRequired={false}
                >
                  {`${props.label}${props.required ? ' *' : ''}`}
                </FormLabel>

                <Input
                  id={props.name}
                  //   placeholder={isFocused ? props.placeholder : ''}
                  type={props.type}
                  {...(field as any)}
                  before={props.before}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  placeholder={props.placeholder}
                  className={{
                    main: `
                         focus block w-full
                         transition-all hover:outline-0 hover:ring-0 focus:ring-0
                         ${field.value && field.value != '' ? 'pt-[16px]' : ''}
                         ${isError && 'border-destructive text-destructive'}
                         ${props.className?.main}
                       `,
                  }}
                />
              </div>
            </FormControl>

            {props.description && (
              <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                {props.description}
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
  ]);

  return <div className={cn('w-full', props.className?.wrapper)}>{content}</div>;
}

export const profileEditSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: z.string().optional(),
    address: z.string().optional(),
    street_number: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    company_buy: z.boolean().optional().default(false),
    company: z.string().optional(),
    ico: z.string().optional(),
    dic: z.string().optional(),
    ic_dph: z.string().optional(),
    gender: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    image: z
      .object({
        id: z.number(),
      })
      .optional(),
    address_temporary: z.string().optional(),
    street_number_temporary: z.string().optional(),
    postcode_temporary: z.string().optional(),
    city_temporary: z.string().optional(),
    country_temporary: z
      .object({
        id: z.string(),
      })
      .optional(),
    email_company: z.string().email().optional(),
    address_company: z.string().optional(),
    street_number_company: z.string().optional(),
    postcode_company: z.string().optional(),
    city_company: z.string().optional(),
    country_company: z
      .object({
        id: z.string(),
      })
      .optional(),
    address_delivery: z.string().optional(),
    street_number_delivery: z.string().optional(),
    postcode_delivery: z.string().optional(),
    city_delivery: z.string().optional(),
    country_delivery: z
      .object({
        id: z.string(),
      })
      .optional(),
    name_delivery: z.string().optional(),
    surname_delivery: z.string().optional(),
    company_delivery: z.string().optional(),
    iban: z.string().optional(),
    swift: z.string().optional(),
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    national_id_number: z.string().optional(),
  });
